import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';

import type { ClaveSimple, ItemSimple } from '../tipos';

/*
 * Las cinco rutas tienen la misma forma (`/admin/<clave>`, `/admin/<clave>/{id}`); se tipan con la
 * de videos y el resultado se toma como el ítem del tipo pedido.
 */
type RutaId = '/api/v1/admin/videos/{id}';
type RutaAlta = '/api/v1/admin/videos';
const rutaId = (c: ClaveSimple) => `/api/v1/admin/${c}/{id}` as RutaId;
const rutaAlta = (c: ClaveSimple) => `/api/v1/admin/${c}` as RutaAlta;
type Cuerpo = Record<string, unknown>;

export function useItemSimple(clave: ClaveSimple, id: number | null) {
  return useQuery({
    queryKey: ['contenido', clave, id],
    queryFn: () =>
      unwrap(
        api.GET(rutaId(clave), { params: { path: { id: String(id) } } }),
      ) as Promise<ItemSimple>,
    enabled: id !== null,
  });
}

export function useGuardarSimple(clave: ClaveSimple, id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Cuerpo) =>
      (id === null
        ? unwrap(api.POST(rutaAlta(clave), { body: body as never }))
        : unwrap(
            api.PATCH(rutaId(clave), {
              params: { path: { id: String(id) } },
              body: body as never,
            }),
          )) as Promise<ItemSimple>,
    onSuccess: (d) => {
      qc.setQueryData(['contenido', clave, d.id], d);
      void qc.invalidateQueries({ queryKey: ['contenido'] });
    },
  });
}

export function useBorrarSimple(clave: ClaveSimple) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(api.DELETE(rutaId(clave), { params: { path: { id: String(id) } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
  });
}

/** Alta masiva de flashcards o guías GPC (un elemento por archivo). */
export function useAltaMasiva(clave: 'flashcards' | 'gpc-guides') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Cuerpo) =>
      unwrap(
        api.POST(`/api/v1/admin/${clave}/batch` as '/api/v1/admin/flashcards/batch', {
          body: body as never,
        }),
      ) as Promise<ItemSimple[]>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
  });
}
